import { useState, useEffect } from 'react';
import { QueryProvider } from '@/lib/react-query';
import { AppRoutes } from './routes';
import { ThemeProvider } from '@/contexts/theme-context';
import { useConnectionKeepAlive } from '@/lib/hooks/useConnectionKeepAlive';
import { useSupabaseHealth } from '@/lib/hooks/useSupabaseHealth';
import { useConnectionStore } from '@/lib/stores/connection-store';
import { ConnectionStatus } from '@/shared/components/ui/connection-status';
// Import role-based styles to hide floating action buttons for participants
import RoleBasedStyles from '@/shared/components/ui/role-based-styles';
// Debug tools removed for production
import Logger from '@/shared/utils/logger';
import { CookieBanner } from '@/components/privacy/CookieBanner';
import './styles/theme.css';
import './styles/role-based.css'; // Import the role-based CSS styles

/**
 * Root application component
 * Sets up the core providers for the application:
 * - React Router for routing
 * - QueryProvider for React Query
 * - AuthProvider for authentication state
 */
/**
 * Root application component
 * Note: BrowserRouter and AuthProvider are now in index.tsx
 */
function App() {
  // Maintain websocket ping with more frequent checks when important operations happen
  useConnectionKeepAlive({
    pingIntervalMs: 3 * 60 * 1000,  // 3 minutes between pings
    monitorIntervalMs: 60 * 1000   // 60 seconds between connection checks (updated) 
  });
  
  // Monitor overall health / credentials
  useSupabaseHealth(60000); // 60s polling interval
  
  // Initialize logging and install console interceptor
  useEffect(() => {
    // Install console interceptor to capture ALL console output
    // Logger.installConsoleInterceptor(); // Method not available
    
    Logger.info('Application started', { context: 'App' });
    Logger.info('Environment', { 
      context: 'App', 
      data: {
        nodeEnv: process.env.NODE_ENV,
        browserInfo: navigator.userAgent
      }
    });
    
    return () => {
      // Clean up by removing console interceptor when app unmounts
      // Logger.uninstallConsoleInterceptor(); // Method not available
    };
  }, []);

  // Get current connection state for conditional rendering
  const { online } = useConnectionStore();
  const [showDisconnectBanner, setShowDisconnectBanner] = useState(false);
  
  // Only show disconnection banner after a consistent disconnection (not just a flicker)
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    
    if (!online) {
      // Wait 5 seconds before showing disconnect banner to avoid flickering
      timeout = setTimeout(() => {
        setShowDisconnectBanner(true);
      }, 5000);
    } else {
      setShowDisconnectBanner(false);
    }
    
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [online]);

  return (
    <ThemeProvider>
      <QueryProvider>
        {/* Add RoleBasedStyles to hide floating action buttons for participants */}
        <RoleBasedStyles sessionId={undefined} />
        {/* Connection status indicator that appears when disconnected for >5 seconds */}
        {showDisconnectBanner && (
          <div className="fixed top-0 left-0 right-0 z-50 p-2 bg-red-600 text-white flex justify-center">
            <ConnectionStatus showRetry={true} />
          </div>
        )}
        <AppRoutes />
        <CookieBanner />
      </QueryProvider>
    </ThemeProvider>
  );
}

export default App;
